import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast, Toaster } from "sonner";
import {
  Sparkles, ArrowUpRight, Copy, Check, RefreshCw, Send, FileText,
  Clock, Zap, ShieldCheck, AlertTriangle, ExternalLink,
  TrendingUp, Server, Mail, Phone, Bot, Share2,
} from "lucide-react";

import HubKpis from "./hub/HubKpis";
import ShareKitModal from "./hub/ShareKitModal";
import DeliverabilityPanel from "./hub/DeliverabilityPanel";
import { Section, MiniStat, PriceCard, Health, fmtDate, fmtDateTime, STATUS_PILL } from "./hub/HubShared";

export default function AIVisibilityHub() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [busyId, setBusyId] = useState("");
  const [shareKit, setShareKit] = useState(null);
  const [shareTab, setShareTab] = useState("linkedin");
  const [copiedChan, setCopiedChan] = useState("");
  const [loadingKit, setLoadingKit] = useState(false);
  const [deliveries, setDeliveries] = useState(null);
  const [resendDiag, setResendDiag] = useState(null);
  const [busyDiag, setBusyDiag] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: d }, { data: dv }] = await Promise.all([
        api.get("/ai-visibility/overview"),
        api.get("/ai-visibility/deliveries").catch(() => ({ data: null })),
      ]);
      setData(d);
      setDeliveries(dv);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const checkResendDomain = async () => {
    setBusyDiag(true);
    try {
      const { data: r } = await api.get("/ai-visibility/resend-diagnostic");
      setResendDiag(r);
      if (r.ok) toast.success(`Resend domains checked · ${r.domains?.length || 0} found.`);
      else toast.error(`Resend check failed: ${r.error}`);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusyDiag(false); }
  };

  const resendOneTeaser = async (leadId) => {
    try {
      const { data: r } = await api.post(`/public-leads/${leadId}/resend-teaser`);
      if (r.email_sent) toast.success(`Teaser re-sent (send #${r.send_count}).`);
      else toast.error(`Send failed: ${r.email_error || "unknown"}`);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const copyAuditLink = () => {
    const url = data?.marketing?.public_audit_url;
    if (!url) return;
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      toast.success("Public /audit link copied.");
      setTimeout(() => setCopied(false), 1800);
    });
  };

  const resendIntake = async (intakeId) => {
    setBusyId(intakeId);
    try {
      await api.post(`/intakes/${intakeId}/send-magic-link`);
      toast.success("Magic link re-sent.");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusyId(""); }
  };

  const generateReport = async (clientId) => {
    setBusyId(clientId);
    try {
      await api.post(`/reports/ai-visibility/${clientId}/regenerate-summary`);
      toast.success("Report summary regenerated.");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusyId(""); }
  };

  const sendReportToClient = async (clientId) => {
    setBusyId(clientId);
    try {
      await api.post(`/reports/ai-visibility/${clientId}/send-to-client`);
      toast.success("Report emailed to client.");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusyId(""); }
  };

  const runQuarterly = async () => {
    try {
      const { data: r } = await api.post("/intakes/run-quarterly-reaudit-now");
      toast.success(`Re-audit scan: created ${r.created || 0}, skipped ${r.skipped || 0}.`);
      load();
    } catch {
      toast.info("Manual re-audit trigger endpoint not wired — the daily cron at 4 AM UTC will handle it.");
    }
  };

  const openShareKit = async () => {
    setLoadingKit(true);
    try {
      const { data: kit } = await api.get("/ai-visibility/share-kit");
      setShareKit(kit);
      setShareTab("linkedin");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoadingKit(false); }
  };

  const copyChannel = (key, content) => {
    navigator.clipboard.writeText(content).then(() => {
      setCopiedChan(key);
      toast.success("Copied to clipboard.");
      setTimeout(() => setCopiedChan(""), 1800);
    });
  };

  if (loading && !data) {
    return <div className="p-8 text-neutral-500 text-sm">Loading AI Visibility overview…</div>;
  }
  if (!data) return null;
  const k = data.kpis || {};
  const h = data.health || {};

  return (
    <div className="p-8 max-w-7xl" data-testid="ai-visibility-hub">
      <Toaster position="top-right" richColors />

      {/* Header */}
      <div className="flex items-start justify-between gap-4 mb-8 flex-wrap">
        <div>
          <div className="overline mb-2 flex items-center gap-2">
            <Sparkles className="w-3.5 h-3.5" /> AI Visibility · Command Hub
          </div>
          <h1 className="text-3xl font-bold text-neutral-900">Manage your entire audit business from one page</h1>
          <p className="text-neutral-600 mt-2 max-w-3xl text-sm">
            Marketing lead-magnet, intake pipeline, generated reports, automated nurture sequence,
            quarterly re-audits and deployment health — all in one place.
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={load} className="lf-btn-outline text-sm flex items-center gap-2" data-testid="hub-refresh-btn">
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
        </div>
      </div>

      <HubKpis kpis={k} />

      {/* Marketing panel */}
      <Section
        title="Marketing — public lead magnet"
        eyebrow="1 · Reach"
        action={
          <button onClick={openShareKit} disabled={loadingKit} className="lf-btn-dark text-sm px-4 py-2 inline-flex items-center gap-2" data-testid="hub-share-kit-btn">
            <Share2 className="w-4 h-4" /> {loadingKit ? "Loading…" : "Generate share kit"}
          </button>
        }
      >
        <div className="grid md:grid-cols-3 gap-4">
          <div className="md:col-span-2 border border-neutral-200 bg-white p-5">
            <div className="text-xs font-mono uppercase tracking-widest text-neutral-500 mb-2">Public /audit URL</div>
            <div className="flex items-center gap-2 mb-3">
              <code className="flex-1 bg-neutral-50 border border-neutral-200 px-3 py-2 text-xs font-mono truncate" data-testid="hub-audit-url">
                {data.marketing?.public_audit_url || "APP_URL not set"}
              </code>
              <button onClick={copyAuditLink} disabled={!data.marketing?.public_audit_url} className="lf-btn-outline text-xs px-3 py-2 flex items-center gap-1" data-testid="hub-copy-audit-url">
                {copied ? <><Check className="w-3.5 h-3.5" /> Copied</> : <><Copy className="w-3.5 h-3.5" /> Copy</>}
              </button>
              <a href={data.marketing?.public_audit_url || "#"} target="_blank" rel="noopener noreferrer" className="lf-btn-outline text-xs px-3 py-2 flex items-center gap-1">
                <ExternalLink className="w-3.5 h-3.5" /> Open
              </a>
            </div>
            <p className="text-sm text-neutral-600">
              Share this link on socials, email footers, cold DMs. Every submission auto-enrolls the prospect in the
              <strong className="text-neutral-900"> Audit teaser → paid audit</strong> nurture (Day-1 SMS · Day-3 email · Day-7 AI voice call).
            </p>
          </div>
          <div className="border border-neutral-200 bg-neutral-900 text-white p-5">
            <div className="text-xs font-mono uppercase tracking-widest text-amber-300 mb-2">This week</div>
            <div className="text-4xl font-bold">{k.audit_leads_7d ?? 0}</div>
            <div className="text-sm text-neutral-400 mt-1">new audit leads captured</div>
            <div className="text-xs text-neutral-500 mt-4 font-mono">
              lifetime · {k.audit_leads_total ?? 0} leads
            </div>
          </div>
        </div>
      </Section>

      {/* Intake pipeline */}
      <Section
        title="Intakes — magic-link forms"
        eyebrow="2 · Discovery"
        action={<Link to="/intakes" className="text-xs text-coral hover:underline inline-flex items-center gap-1" data-testid="hub-intakes-full-link">View full pipeline <ArrowUpRight className="w-3 h-3" /></Link>}
      >
        <div className="border border-neutral-200 bg-white overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-neutral-50 text-neutral-600 uppercase tracking-widest text-xs font-mono">
              <tr>
                <th className="text-left px-4 py-3">Client</th>
                <th className="text-left px-4 py-3">Created</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-left px-4 py-3">Emails</th>
                <th className="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {(data.recent_intakes || []).length === 0 && (
                <tr><td colSpan={5} className="text-center py-8 text-neutral-400" data-testid="hub-intakes-empty">
                  No intakes yet. They auto-create when a client activates an AI Visibility add-on.
                </td></tr>
              )}
              {(data.recent_intakes || []).map((i) => (
                <tr key={i.id} className="border-t border-neutral-100 hover:bg-neutral-50">
                  <td className="px-4 py-3">
                    <Link to={`/clients/${i.client_id}`} className="font-medium hover:underline" data-testid={`hub-intake-client-${i.id.slice(0,8)}`}>
                      {i.client_business_name || "—"}
                    </Link>
                    <div className="text-xs text-neutral-400">{i.client_email}</div>
                  </td>
                  <td className="px-4 py-3 text-xs text-neutral-500 font-mono">{fmtDateTime(i.created_at)}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs font-mono uppercase tracking-wider px-2 py-0.5 border ${STATUS_PILL[i.status] || STATUS_PILL.pending}`}>
                      {i.status || "pending"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs text-neutral-500 font-mono">{i.email_send_count || 0} sent</td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => resendIntake(i.id)}
                      disabled={busyId === i.id}
                      className="text-xs text-neutral-600 hover:text-neutral-900 inline-flex items-center gap-1 disabled:opacity-50"
                      data-testid={`hub-intake-resend-${i.id.slice(0,8)}`}
                    >
                      <Send className="w-3 h-3" /> {busyId === i.id ? "…" : "Re-send link"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      {/* Reports */}
      <Section
        title="Reports — generated audits"
        eyebrow="3 · Deliver"
        action={<Link to="/reports" className="text-xs text-coral hover:underline inline-flex items-center gap-1" data-testid="hub-reports-full-link">All reports <ArrowUpRight className="w-3 h-3" /></Link>}
      >
        <div className="border border-neutral-200 bg-white overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-neutral-50 text-neutral-600 uppercase tracking-widest text-xs font-mono">
              <tr>
                <th className="text-left px-4 py-3">Client</th>
                <th className="text-left px-4 py-3">Generated</th>
                <th className="text-left px-4 py-3">Exec summary</th>
                <th className="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {(data.recent_reports || []).length === 0 && (
                <tr><td colSpan={4} className="text-center py-8 text-neutral-400" data-testid="hub-reports-empty">
                  No reports generated yet. Once a client submits an intake, generate the audit from their detail page.
                </td></tr>
              )}
              {(data.recent_reports || []).map((r) => (
                <tr key={r.id} className="border-t border-neutral-100 hover:bg-neutral-50">
                  <td className="px-4 py-3">
                    <Link to={`/clients/${r.client_id}`} className="font-medium hover:underline">
                      {r.client_business_name}
                    </Link>
                    <div className="text-xs text-neutral-400">{r.client_email}</div>
                  </td>
                  <td className="px-4 py-3 text-xs text-neutral-500 font-mono">{fmtDateTime(r.generated_at)}</td>
                  <td className="px-4 py-3 text-xs">
                    {r.executive_summary_generated_at ? (
                      <span className="text-emerald-700 inline-flex items-center gap-1"><Check className="w-3 h-3" /> AI-drafted</span>
                    ) : (
                      <span className="text-neutral-400">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex gap-3">
                      <button onClick={() => generateReport(r.client_id)} disabled={busyId === r.client_id} className="text-xs text-neutral-600 hover:text-neutral-900 inline-flex items-center gap-1 disabled:opacity-50" data-testid={`hub-report-regen-${r.client_id.slice(0,8)}`}>
                        <RefreshCw className="w-3 h-3" /> Regenerate
                      </button>
                      <button onClick={() => sendReportToClient(r.client_id)} disabled={busyId === r.client_id} className="text-xs text-neutral-600 hover:text-neutral-900 inline-flex items-center gap-1 disabled:opacity-50" data-testid={`hub-report-send-${r.client_id.slice(0,8)}`}>
                        <Mail className="w-3 h-3" /> Email client
                      </button>
                      <Link to={`/reports/ai-visibility?client_id=${r.client_id}`} className="text-xs text-coral hover:underline inline-flex items-center gap-1">
                        Open <ArrowUpRight className="w-3 h-3" />
                      </Link>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      {/* Automation */}
      <Section title="Automation" eyebrow="4 · Convert">
        <div className="grid md:grid-cols-2 gap-4">
          <div className="border border-neutral-200 bg-white p-5">
            <div className="flex items-center gap-2 mb-3">
              <Zap className="w-4 h-4 text-amber-600" />
              <div className="font-semibold text-neutral-900">Audit teaser → paid audit</div>
            </div>
            <p className="text-sm text-neutral-600 mb-4">
              3-touch nurture · Day-1 SMS · Day-3 email · Day-7 <strong>AI voice call</strong>.
              Fires automatically on every /audit submission.
            </p>
            <div className="grid grid-cols-3 gap-2 mb-4 text-center">
              <MiniStat label="Active"    value={data.audit_sequence?.active_runs ?? 0}    accent="amber" />
              <MiniStat label="Completed" value={data.audit_sequence?.completed_runs ?? 0} accent="emerald" />
              <MiniStat label="Failed"    value={data.audit_sequence?.failed_steps ?? 0}   accent="rose" />
            </div>
            <Link to="/sequences" className="text-xs text-coral hover:underline inline-flex items-center gap-1" data-testid="hub-sequence-link">
              Manage sequences <ArrowUpRight className="w-3 h-3" />
            </Link>
          </div>
          <div className="border border-neutral-200 bg-white p-5">
            <div className="flex items-center gap-2 mb-3">
              <Clock className="w-4 h-4 text-blue-600" />
              <div className="font-semibold text-neutral-900">Quarterly re-audits</div>
            </div>
            <p className="text-sm text-neutral-600 mb-4">
              Every AI Visibility Pro subscriber gets a fresh audit every 90 days.
              APScheduler cron fires daily at 4:00 AM UTC and only creates a new intake when due.
            </p>
            <div className="text-xs text-neutral-500 font-mono mb-4">
              Next due: <strong className="text-neutral-900">{fmtDate(k.next_reaudit_due) || "—"}</strong>
            </div>
            <button onClick={runQuarterly} className="lf-btn-outline text-xs px-3 py-2 inline-flex items-center gap-1" data-testid="hub-run-reaudit-btn">
              <RefreshCw className="w-3 h-3" /> Run scan now
            </button>
          </div>
        </div>
      </Section>

      {/* Add-ons / revenue */}
      <Section title="Revenue — AI Visibility add-ons" eyebrow="5 · Monetize">
        <div className="grid md:grid-cols-2 gap-4">
          <PriceCard
            title="AI Visibility Audit"
            price={data.addons?.ai_visibility_audit?.price_monthly || 79}
            desc="Self-serve. 30-point audit + competitor benchmark. Client gets the PDF."
          />
          <PriceCard
            title="AI Visibility Pro"
            price={data.addons?.ai_visibility_pro?.price_monthly || 299}
            desc="Done-for-you. We implement every fix + auto re-audit every 90 days."
            accent
          />
        </div>
        <Link to="/addons" className="text-xs text-coral hover:underline inline-flex items-center gap-1 mt-3">
          Manage add-on catalog & Stripe prices <ArrowUpRight className="w-3 h-3" />
        </Link>
      </Section>

      {/* Deliverability panel */}
      <DeliverabilityPanel
        deliveries={deliveries}
        resendDiag={resendDiag}
        busyDiag={busyDiag}
        onCheckDomain={checkResendDomain}
        onResendOne={resendOneTeaser}
      />

      {/* Deployment health */}
      <Section title="Deployment health" eyebrow="7 · Operate">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Health ok={h.resend_configured}    label={h.resend_from ? `Resend · ${(h.resend_from.match(/<([^>]+)>/) || [null, h.resend_from])[1]}` : "Resend (email)"} icon={Mail} />
          <Health ok={h.vapi_configured}      label="Vapi (voice)"   icon={Phone} />
          <Health ok={h.twilio_configured}    label="Twilio (SMS)"   icon={Bot} />
          <Health ok={h.emergent_llm_key}     label="Emergent LLM"   icon={Sparkles} />
          <Health ok={h.weasyprint_installed} label="Weasyprint PDF" icon={FileText} />
          <Health ok={h.app_url_configured && !h.app_url_is_preview}
                  label="APP_URL"
                  icon={Server}
                  warn={h.app_url_is_preview}
                  warnLabel="Preview host" />
          <Health ok label="APScheduler" icon={Clock} />
          <Health ok label="RBAC + audit log" icon={ShieldCheck} />
        </div>
        {h.app_url_is_preview && (
          <div className="mt-3 border border-amber-300 bg-amber-50 text-amber-900 text-sm p-3 flex items-start gap-2" data-testid="hub-app-url-warning">
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
            <div>
              <strong>APP_URL still points at the preview domain.</strong> Update <code>backend/.env APP_URL</code> to
              your production host so Vapi webhooks (Day-7 audit calls, missed-call rescue) survive redeploys.
            </div>
          </div>
        )}
      </Section>

      {/* Share Kit modal */}
      {shareKit && (
        <ShareKitModal
          kit={shareKit}
          tab={shareTab}
          setTab={setShareTab}
          copiedChan={copiedChan}
          onCopy={copyChannel}
          onClose={() => setShareKit(null)}
        />
      )}
    </div>
  );
}
